import React, { forwardRef, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import { X } from 'lucide-react';

export interface ModalProps {
  /** Whether the modal is visible */
  isOpen: boolean;
  /** Callback when the modal should close */
  onClose: () => void;
  /** Modal title */
  title: string;
  /** Optional description below the title */
  description?: string;
  /** Panel max-width */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Modal body content */
  children: React.ReactNode;
  /** Additional class names for the panel */
  className?: string;
}

const sizeClasses: Record<NonNullable<ModalProps['size']>, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
};

export const Modal = forwardRef<HTMLDivElement, ModalProps>(
  ({ isOpen, onClose, title, description, size = 'md', children, className }, ref) => {
    const panelRef = useRef<HTMLDivElement>(null);
    const titleId = `modal-title-${React.useId()}`;

    // Combine forwarded ref with internal ref
    const setRef = useCallback(
      (node: HTMLDivElement | null) => {
        (panelRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
        }
      },
      [ref],
    );

    // Escape key closes modal
    useEffect(() => {
      if (!isOpen) return;
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          onClose();
        }
      };
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);

    // Lock body scroll
    useEffect(() => {
      if (isOpen) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
      return () => {
        document.body.style.overflow = '';
      };
    }, [isOpen]);

    // Focus trap
    useEffect(() => {
      if (!isOpen || !panelRef.current) return;

      const panel = panelRef.current;
      const focusableSelector =
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

      // Focus first focusable element
      const firstFocusable = panel.querySelector<HTMLElement>(focusableSelector);
      firstFocusable?.focus();

      const handleTab = (e: KeyboardEvent) => {
        if (e.key !== 'Tab') return;
        const focusableElements = panel.querySelectorAll<HTMLElement>(focusableSelector);
        if (focusableElements.length === 0) return;

        const first = focusableElements[0];
        const last = focusableElements[focusableElements.length - 1];

        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      };

      window.addEventListener('keydown', handleTab);
      return () => window.removeEventListener('keydown', handleTab);
    }, [isOpen]);

    if (!isOpen) return null;

    const handleBackdropClick = (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    };

    return createPortal(
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        onClick={handleBackdropClick}
      >
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm volt-animate-fade-in" />

        {/* Panel */}
        <div
          ref={setRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          className={clsx(
            'relative w-full',
            sizeClasses[size],
            'bg-[var(--volt-dark-surface)] border border-[var(--volt-dark-border)]',
            'rounded-[var(--volt-radius-xl)] shadow-[var(--volt-shadow-lg)]',
            'volt-animate-scale-up',
            'flex flex-col max-h-[85vh]',
            className,
          )}
        >
          {/* Header */}
          <div className="flex items-start justify-between gap-4 px-6 pt-6 pb-4 border-b border-[var(--volt-dark-border)]">
            <div className="flex-1 min-w-0">
              <h2
                id={titleId}
                className="text-lg font-semibold text-[var(--volt-text-primary)]"
              >
                {title}
              </h2>
              {description && (
                <p className="mt-1 text-sm text-[var(--volt-text-muted)]">{description}</p>
              )}
            </div>
            <button
              onClick={onClose}
              className={clsx(
                'p-1.5 rounded-[var(--volt-radius-md)]',
                'text-[var(--volt-text-muted)]',
                'hover:text-[var(--volt-text-primary)] hover:bg-[var(--volt-dark-elevated)]',
                'transition-colors duration-[var(--volt-transition-fast)]',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--volt-blue)]',
              )}
              aria-label="Close modal"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {children}
          </div>
        </div>
      </div>,
      document.body,
    );
  },
);

Modal.displayName = 'Modal';
